import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { clearCourse } from '../../store/courseSlice';
import { stopImpersonating } from '../../store/impersonationSlice';
import { clearUser, setUser } from '../../store/userSlice';
import { logIn, logOut, observeAuth } from '../../auth';
import MyStudySidekick from '../common/MyStudySidekick';
import { AUTH_STATUS } from '../common/constants';
import { getFriendlyAuthError, getIdentityUser, getShibbolethProviders } from '../common/helpers';

/**
 * Require an authorized Shibboleth session before rendering the standalone
 * developer portal.
 *
 * Canvas routes use their existing LTI authentication and do not pass through
 * this component.
 */
function IdentityGate({ children }) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(AUTH_STATUS.CHECKING);
  const [error, setError] = useState(null);
  const [providerOptions, setProviderOptions] = useState([]);
  const [signingInProviderId, setSigningInProviderId] = useState(null);

  useEffect(() => {
    /*
     * Remove any stale Canvas-shaped profile before the auth observer restores
     * the standalone Shibboleth profile. API routing is based on the current
     * browser route, not this Redux value.
     */
    dispatch(clearUser());

    let isActive = true;
    let unsubscribe;
    try {
      setProviderOptions(getShibbolethProviders());
      unsubscribe = observeAuth(async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const identityUser = await getIdentityUser(firebaseUser);
            if (!isActive) return;

            if (!identityUser.canAccessPortal) {
              dispatch(clearUser());
              dispatch(stopImpersonating());
              dispatch(clearCourse());
              setStatus(AUTH_STATUS.DENIED);
              setError(null);
              return;
            }

            dispatch(setUser(identityUser));

            // Persisted impersonation state must not survive when the newly
            // authenticated user does not have the required entitlement.
            if (!identityUser.canImpersonate) {
              dispatch(stopImpersonating());
            }

            setStatus(AUTH_STATUS.SIGNED_IN);
            setError(null);
          } catch (profileError) {
            if (!isActive) return;
            dispatch(clearUser());
            dispatch(stopImpersonating());
            setError(getFriendlyAuthError(profileError));
            setStatus(AUTH_STATUS.SIGNED_OUT);
          }
          return;
        }

        // A signed-out user should not retain state from an earlier portal session.
        dispatch(clearUser());
        dispatch(stopImpersonating());
        dispatch(clearCourse());
        setStatus(AUTH_STATUS.SIGNED_OUT);
      });
    } catch (configurationError) {
      setError(getFriendlyAuthError(configurationError));
      setStatus(AUTH_STATUS.SIGNED_OUT);
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [dispatch]);

  const handleSignIn = async (providerId) => {
    setSigningInProviderId(providerId);
    setError(null);

    try {
      /*
       * The auth observer above changes the screen after the popup completes.
       * Keeping that state transition in one place also handles restored sessions.
       */
      await logIn(providerId);
    } catch (signInError) {
      setError(getFriendlyAuthError(signInError));
    } finally {
      setSigningInProviderId(null);
    }
  };

  if (status === AUTH_STATUS.CHECKING) {
    return (
      <Box className="mss__authLoading" role="status" aria-label="Checking sign-in status">
        <CircularProgress />
      </Box>
    );
  }

  if (status === AUTH_STATUS.SIGNED_OUT) {
    return (
      <Box className="mss__authPage">
        <Paper className="mss__authCard" elevation={3}>
          <img src="/img/oduCrown_blue.svg" alt="" className="oduCrown" />
          <MyStudySidekick component="h1" />
          <Typography component="h2" variant="h5">
            Developer Portal
          </Typography>
          <Typography>
            Sign in with an authorized ODU MIDAS account to continue. Choose the Shibboleth
            environment where your account is available. Canvas launches do not use this sign-in
            page.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack spacing={1}>
            {providerOptions.map((provider) => (
              <Button
                key={provider.providerId}
                variant="contained"
                className="mss__button primary"
                onClick={() => handleSignIn(provider.providerId)}
                disabled={signingInProviderId !== null}
              >
                {signingInProviderId === provider.providerId
                  ? 'Signing in…'
                  : `Sign in with ${provider.label}`}
              </Button>
            ))}
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (status === AUTH_STATUS.DENIED) {
    return (
      <Box className="mss__authPage">
        <Paper className="mss__authCard" elevation={3}>
          <img src="/img/oduCrown_blue.svg" alt="" className="oduCrown" />
          <MyStudySidekick component="h1" />
          <Typography component="h2" variant="h5">
            Developer Portal
          </Typography>
          <Alert severity="error">You do not have access to the Developer Portal</Alert>
          <Button variant="contained" className="mss__button primary" onClick={logOut}>
            Sign out
          </Button>
        </Paper>
      </Box>
    );
  }

  return children;
}

export default IdentityGate;
